import { Inject, Injectable, NgZone } from '@angular/core';
import { UAParser } from 'ua-parser-js';
import { v4 as uuidv4 } from 'uuid';
import { PI } from '../constants-types';
import { CURRENT_APP_TYPE, InitData } from '../core-data-model';
import { AppType } from '../shared/Enums';
import { Logger } from '../system-services';

@Injectable({
    providedIn: 'root'
})
export class DataCollectionService {
    // track user from login to login.
    sessionId = localStorage.getItem('session_id');
    // track user within module from arrival to departure.
    moduleId = null;
    userGuid = null;
    companyGuid = null;
    uaParser = UAParser(navigator.userAgent);

    constructor(
        private logger: Logger,
        private ngZone: NgZone,
        @Inject(CURRENT_APP_TYPE) private appType: AppType
    ) {}

    sendDataToServer(data: any): void {
        this.ngZone.runOutsideAngular(() => {
            fetch(
                PI.appSettings.dataCollectionHostUrl +
                    PI.appSettings.dataCollectionUrl,
                {
                    method: 'POST',
                    body: JSON.stringify(data),
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    keepalive: true
                }
            ).catch((err) =>
                this.logger.logError(
                    'Error occurred during frontend data collection. Error: ' +
                        err,
                    JSON.stringify(data)
                )
            );
        });
    }

    handleClickEvent(event: any): void {
        const elementAttributes = {};
        if (event.target.attributes?.length > 0) {
            Array.from(event.target.attributes).forEach((attr: any) => {
                if (!attr.name?.includes('_ngcontent')) {
                    elementAttributes[attr.name] = attr.value;
                }
            });
        }

        // remove the last 4 elements from the array, because 'Window' and 'document' don't have localName,
        // resulting in undefined values in the array, and 'html' and 'body' they are common among all paths
        let eventPath = event.path?.map((el: any) => el.localName);
        if (eventPath?.length >= 4) {
            eventPath = eventPath.slice(0, eventPath.length - 4);
        }

        const payload = {
            userGuid: this.userGuid,
            companyGuid: this.companyGuid,
            sessionId: this.sessionId,
            moduleId: this.moduleId,
            path: window.location.pathname,
            currentProductModule:
                AppType[this.getProductModule(window.location.pathname)],
            className: event.target.className,
            elementName: event.target.localName,
            eventName: event.type,
            eventPath: eventPath,
            eventTarget: elementAttributes,
            eventTime: Date.now(),
            innerText: event.target.innerText?.slice(0, 40),
            clientX: event.clientX,
            clientY: event.clientY
        };
        this.sendDataToServer(payload);
    }

    initializeDataCollection(initData: InitData): void {
        window.addEventListener('click', this.handleClickEvent.bind(this));
        if (!this.moduleId) {
            this.moduleId = uuidv4();
        }

        this.userGuid = initData.administrator.userGuid;
        this.companyGuid = initData.administrator.companyId;

        const payload = {
            eventName: 'InitUser',
            eventTime: Date.now(),
            currentProductModule:
                AppType[this.getProductModule(window.location.pathname)],
            os: this.uaParser.os,
            browser: this.uaParser.browser,
            device: this.uaParser.device,
            engine: this.uaParser.engine,
            cpu: this.uaParser.cpu,
            userAgent: this.uaParser.ua,
            userGuid: this.userGuid,
            companyGuid: this.companyGuid,
            sessionId: this.sessionId,
            moduleId: this.moduleId,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };

        this.sendDataToServer(payload);
    }

    // as of 2022 Q3, Diagnose and Inspire is embedded in SPA, so trying to distinguish them using the path
    getProductModule(path: string) {
        if (this.appType !== AppType.Main) {
            return this.appType;
        } else if (path.startsWith('/admin') || path.startsWith('/pi/admin')) {
            return AppType.Admin;
        } else if (
            path.startsWith('/design') ||
            path.startsWith('/pi/design')
        ) {
            return AppType.Design;
        } else if (path.startsWith('/hire') || path.startsWith('/pi/hire')) {
            return AppType.Hire;
        } else if (
            path.startsWith('/engage') ||
            path.startsWith('/pi/engage')
        ) {
            return AppType.Diagnose;
        } else if (
            path.startsWith('/inspire') ||
            path.startsWith('/pi/inspire')
        ) {
            return AppType.Inspire;
        }
        return AppType.Main;
    }
}
